import { Component, EventEmitter, OnInit } from '@angular/core';
import { AssetService } from '../asset.service';
import { Asset } from '../asset';
import { NotificationService } from '../../shared/notification.service';

@Component({
  selector: 'app-asset-list',
  templateUrl: 'list.component.html',
  styleUrls: [],
})
export class AssetListComponent implements OnInit {
  public asset: Asset;

  /**
   * A list of all the assets currently loaded.
   *
   * @type {Asset[]}
   */
  public assets: Asset[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of assets.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of assets that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of assets that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a asset has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public assetDeleted: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {AssetService} assetService
   * @param {NotificationService} notificationService
   */
  constructor(
    private assetService: AssetService,
    private notificationService: NotificationService,
  ) {
    this.assetService.loading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.assetService.totalPages.subscribe(totalPages => {
      this.totalPages = totalPages;
    });
    this.assetService.currentPage.subscribe(currentPage => {
      this.currentPage = currentPage;
    });
    this.assetService.totalResults.subscribe(totalResults => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the asset list.
   */
  public ngOnInit(): void {
    this.loadAssets(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * asset list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadAssets(pageNumber);
  }

  /**
   * Loads a list of assets using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadAssets(page?: number, limit?: number): void {
    const listAssets = this.assetService
      .listAssets(page, limit)
      .subscribe((assets: Asset[]) => {
        this.assets = assets;
        listAssets.unsubscribe();
      });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate asset ID
   * to delete the asset.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.assetService.deleteAsset(id).subscribe(
      deleted => {
        this.notificationService.success(
          `Asset Deleted`,
          `Asset with ID ${id} was deleted.`,
          5,
        );
        this.assetDeleted.next(true);
        this.onPageChanged(this.currentPage);
      },
      err => {
        this.notificationService.error(
          `Asset Not Deleted`,
          err.error.message,
          10,
        );
        this.assetDeleted.next(false);
        this.onPageChanged(this.currentPage);
      },
    );
  }
}
